import { useEffect, useState } from "react";
import { Checkbox, Icon, IconButton, Input, Select, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { ChecklistItem } from "../lib/api.js";
import "./task-checklist.css";

/**
 * A task's checklist: small ordered steps that do not deserve a task of their
 * own. Every mutation returns the whole list, which replaces local state, so
 * the screen cannot drift from the server's order.
 */

type Person = { id: string; displayName: string };

interface Props {
  taskId: string;
  initial: ChecklistItem[];
  people: Person[];
  canEdit: boolean;
  /** Told the new counts after each change, so the task row can follow. */
  onCounts?: (total: number, done: number) => void;
}

export function TaskChecklist({ taskId, initial, people, canEdit, onCounts }: Props) {
  const { toast } = useToast();
  const [items, setItems] = useState(initial);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  useEffect(() => setItems(initial), [initial]);

  const apply = (next: ChecklistItem[]) => {
    setItems(next);
    onCounts?.(next.length, next.filter((i) => i.done).length);
  };

  const run = (request: Promise<{ items: ChecklistItem[] }>) =>
    request.then((res) => apply(res.items)).catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not save that change.", { tone: "danger" }));

  const base = `/tasks/${taskId}/checklist`;
  const done = items.filter((i) => i.done).length;

  const move = (index: number, by: -1 | 1) => {
    const ids = items.map((i) => i.id);
    const target = index + by;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    void run(api.post<{ items: ChecklistItem[] }>(`${base}/reorder`, { itemIds: ids }));
  };

  if (!canEdit && items.length === 0) return null;

  return (
    <div className="task-detail__section task-checklist">
      <div className="task-detail__section-header">
        <h3 className="task-detail__section-title">Checklist</h3>
        {items.length > 0 && (
          <span className="task-detail__subtask-progress">
            {done} / {items.length}
          </span>
        )}
      </div>

      {items.map((item, index) => (
        <div key={item.id} className="task-checklist__item">
          <Checkbox
            checked={item.done}
            disabled={!canEdit}
            aria-label={`Mark "${item.text}" ${item.done ? "not done" : "done"}`}
            onChange={(e) => void run(api.patch<{ items: ChecklistItem[] }>(`${base}/${item.id}`, { done: e.target.checked }))}
          />
          {editingId === item.id ? (
            <input
              className="task-checklist__edit"
              aria-label="Checklist item text"
              value={editText}
              autoFocus
              onChange={(e) => setEditText(e.target.value)}
              onBlur={() => {
                const text = editText.trim();
                setEditingId(null);
                if (text && text !== item.text) void run(api.patch<{ items: ChecklistItem[] }>(`${base}/${item.id}`, { text }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  setEditText(item.text);
                  setEditingId(null);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className={`task-checklist__text ${item.done ? "is-done" : ""}`}
              disabled={!canEdit}
              onClick={() => {
                setEditingId(item.id);
                setEditText(item.text);
              }}
            >
              {item.text}
            </button>
          )}

          {canEdit ? (
            <>
              <Select
                aria-label={`Assignee for "${item.text}"`}
                className="task-checklist__assignee"
                value={item.assigneeId ?? ""}
                onChange={(e) => void run(api.patch<{ items: ChecklistItem[] }>(`${base}/${item.id}`, { assigneeId: e.target.value || null }))}
              >
                <option value="">Unassigned</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </Select>
              <IconButton icon={<Icon name="chevron-up" size={13} />} label="Move up" size="sm" disabled={index === 0} onClick={() => move(index, -1)} />
              <IconButton icon={<Icon name="chevron-down" size={13} />} label="Move down" size="sm" disabled={index === items.length - 1} onClick={() => move(index, 1)} />
              <IconButton
                icon={<Icon name="trash" size={13} />}
                label={`Delete "${item.text}"`}
                size="sm"
                onClick={() => void run(api.delete<{ items: ChecklistItem[] }>(`${base}/${item.id}`))}
              />
            </>
          ) : (
            item.assigneeName && <span className="task-checklist__who">{item.assigneeName}</span>
          )}
        </div>
      ))}

      {canEdit && (
        <Input
          aria-label="Add a checklist item"
          placeholder="Add an item — press Enter"
          value={draft}
          maxLength={500}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            const text = draft.trim();
            if (!text) return;
            setDraft("");
            void run(api.post<{ items: ChecklistItem[] }>(base, { text }));
          }}
        />
      )}
    </div>
  );
}
